import { withFormValidation } from '@updater-components/form-element';
import {
  withHTMLComponent,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';
import styles from './index.css';
import './option/index.js';

// https://stackoverflow.com/questions/5899783/detect-safari-using-jquery
const parseSafariVersion = (userAgent) =>
  (userAgent.match(/^(?:(?!chrome|android).)*Version\/([\d.]+).*safari/i) ||
    [])[1] || null;

const applySafariReflowFix = (element) => {
  if (parseInt(parseSafariVersion(navigator.userAgent), 10) < 11) {
    // Safari 10 and below fails to update styles in response to classes being added/removed.
    // https://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
    const previousDisplay = element.style.display;
    element.style.display = 'none';
    element.offsetHeight;
    element.style.display = previousDisplay;
  }
};

const KEYS = {
  DOWN: 40,
  LEFT: 37,
  RIGHT: 39,
  UP: 38,
};

const OBSERVED_ATTRS = ['disabled', 'required', 'name', 'value'];

const html = `
  <input type="radio" id="input" tabindex="-1" />
  <slot></slot>
`;

class RadioGroup extends withFormValidation(withHTMLComponent(styles, html)()) {
  static observedAttributes = OBSERVED_ATTRS;
  static reflectedAttributes = OBSERVED_ATTRS.concat([
    'aria-checked',
    'aria-disabled',
    'checked',
    'role',
  ]);
  static attributeTypes = {
    disabled: 'boolean',
    checked: 'boolean',
    required: 'boolean',
  };

  _state = {
    options: [],
    validated: false,
  };

  constructor() {
    super();
    this._input = this.shadowRoot.querySelector('#input');
  }

  connected() {
    this.role = this.role || 'radiogroup';

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeydown);
    this.addEventListener(
      'updradiooptionconnected',
      this._handleConnectedOption
    );
    this.addEventListener(
      'updradiooptiondisconnected',
      this._handleDisconnectedOption
    );
    this.addEventListener('updvalidation', this._handleValidation);
  }

  disconnected() {
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._handleKeydown);
    this.removeEventListener(
      'updradiooptionconnected',
      this._handleConnectedOption
    );
    this.removeEventListener(
      'updradiooptiondisconnected',
      this._handleDisconnectedOption
    );
    this.removeEventListener('updvalidation', this._handleValidation);
  }

  updated(
    { _state: { options }, value, name, disabled, required },
    { value: oldValue }
  ) {
    this.ariaDisabled = disabled.toString();

    applyAsAttributes(this._input, {
      checked: value,
      disabled,
      name,
      required,
    });

    options.forEach((option) => {
      const isSelected = option.value === value;
      option.ariaChecked = isSelected.toString();
      option.tabIndex = isSelected ? 0 : -1;
      option.disabled = option.disabled || disabled;
    });

    if (!value) {
      (options[0] || {}).tabIndex = 0;
    }

    if (value !== oldValue) {
      this.checkValidity();
    }

    applySafariReflowFix(this);
  }

  _commitValue(value) {
    if (this.value !== value) {
      this.value = value;

      // Only dispatch `change` if `value` was changed through user interaction.
      this.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          composed: true,
          detail: value,
        })
      );
    }
  }

  _shiftFocus(direction) {
    const options = this._state.options.filter((opt) => !opt.disabled);
    const index = options.findIndex((option) => option.tabIndex === 0);
    const { length } = options;
    const focusOption = options[(index + length + direction) % length];

    this._commitValue(focusOption.value);
    focusOption && focusOption.focus();
  }

  _handleClick = ({ target: { localName, value } }) => {
    if (localName === 'upd-radio-option') {
      this._commitValue(value);
    }
  };

  _handleConnectedOption = (event) => {
    event.stopPropagation();
    this.mergeState({ options: [...this._state.options, event.target] });
  };

  _handleDisconnectedOption = (event) => {
    event.stopPropagation();
    this.mergeState({
      options: this._state.options.filter((option) => option !== event.target),
    });
  };

  _handleKeydown = (event) => {
    switch (event.keyCode) {
      case KEYS.LEFT:
      case KEYS.UP:
        event.preventDefault();
        this._shiftFocus(-1);
        break;
      case KEYS.RIGHT:
      case KEYS.DOWN:
        event.preventDefault();
        this._shiftFocus(1);
        break;
    }
  };

  _handleValidation = (event) => {
    // Since we can't use host-context (it's in the spec, but only Chrome has
    // implemented) for children we walk each child node and apply
    // upd-validated and upd-invalid classes when appropriate.
    composedTreeWalk(this.shadowRoot, (node) => {
      node.classList.add('upd-validated');
      !this.validity.valid
        ? node.classList.add('upd-invalid')
        : node.classList.remove('upd-invalid');
    });
  };
}

window.customElements.define('upd-radio-group', RadioGroup);

// TODO: Consolidate these utilities into a package
//
// Recursively walk the composed tree from node.
// Taken and slightly modified from
// https://github.com/WICG/inert/blob/master/src/inert.js#L607
function composedTreeWalk(node, callback) {
  if (node.nodeType === Node.ELEMENT_NODE) {
    if (callback) {
      callback(node);
    }

    // Descend into node:
    // If it has a ShadowRoot, ignore all child elements - these will be picked
    // up by the <slot> or <shadow> elements. Descend straight into the
    // ShadowRoot.
    const shadowRoot = node.shadowRoot;
    if (shadowRoot) {
      composedTreeWalk(shadowRoot, callback);
      return;
    }

    // If it is a <slot> element, descend into assigned nodes - these
    // are elements from outside the shadow root which are rendered inside the
    // shadow DOM.
    if (node.localName === 'slot') {
      const distributedNodes = node.assignedNodes
        ? node.assignedNodes({ flatten: true })
        : [];
      distributedNodes.forEach((distributedNode) => {
        composedTreeWalk(distributedNode, callback);
      });
      return;
    }
  }

  // If it is neither the parent of a ShadowRoot, a <slot> element, nor a <shadow> element recurse normally.
  let child = node.firstChild;
  while (child != null) {
    composedTreeWalk(child, callback);
    child = child.nextSibling;
  }
}
